import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  useTheme,
  alpha,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { hasActionPermission } from '../utils/permissions';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const Earnings = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterDay, setFilterDay] = useState(new Date().getDate());
  const [dataFilter, setDataFilter] = useState('current_month');
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState('admin');
  
  // Pagination state for each table
  const [transferFeePage, setTransferFeePage] = useState(0);
  const [transferFeeRowsPerPage, setTransferFeeRowsPerPage] = useState(10);
  const [electronicLevyPage, setElectronicLevyPage] = useState(0);
  const [electronicLevyRowsPerPage, setElectronicLevyRowsPerPage] = useState(10);
  const [payscribePage, setPayscribePage] = useState(0);
  const [payscribeRowsPerPage, setPayscribeRowsPerPage] = useState(10);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        setUserRole(userData.type || 'admin');
        setUserPermissions(userData.permissions || []);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  useEffect(() => {
    fetchEarnings();
  }, [dataFilter, filterMonth, filterYear, filterDay]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      
      let monthToUse = null;
      let yearToUse = null;
      let dayToUse = null;
      let filterType = null;
      
      if (dataFilter === 'current_month') {
        const currentDate = new Date();
        monthToUse = currentDate.getMonth() + 1;
        yearToUse = currentDate.getFullYear();
      } else if (dataFilter === 'month') {
        monthToUse = filterMonth;
        yearToUse = filterYear;
      } else if (dataFilter === 'year') {
        yearToUse = filterYear;
        monthToUse = null;
      } else if (dataFilter === 'day') {
        monthToUse = filterMonth;
        yearToUse = filterYear;
        dayToUse = filterDay;
      } else if (['today', 'last_7_days', 'last_30_days', 'last_3_months', 'last_6_months', 'this_quarter', 'last_quarter', 'this_year'].includes(dataFilter)) {
        filterType = dataFilter;
      }
      
      const response = await api.dashboard.getEarnings(monthToUse, yearToUse, dayToUse, filterType);
      if (response && response.success) {
        setEarnings(response.data);
      } else {
        const errorMsg = response?.message || 'Failed to fetch earnings';
        console.error('Earnings API error:', response);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      console.error('Error details:', error.response || error.message);
      toast.error(error.response?.data?.message || error.message || 'Failed to fetch earnings data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatCard = ({ title, value, icon, iconColor, subtitle }) => (
    <Card
      sx={{
        borderRadius: 3,
        background: theme => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(iconColor || theme.palette.primary.main, 0.05)} 100%)`,
        boxShadow: theme => `0 4px 20px ${alpha(iconColor || theme.palette.primary.main, 0.08)}`,
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme => `0 8px 24px ${alpha(iconColor || theme.palette.primary.main, 0.12)}`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme => `linear-gradient(135deg, ${alpha(iconColor || theme.palette.primary.main, 0.1)} 0%, ${alpha(iconColor || theme.palette.primary.main, 0.2)} 100%)`,
              color: iconColor || theme.palette.primary.main,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} color="primary" sx={{ mt: 1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  if (!hasActionPermission('view_financial_data', userPermissions, userRole)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          You don't have permission to view earnings data.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner size="medium" />;
  }

  if (!earnings) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No earnings data available
        </Typography>
      </Box>
    );
  }

  const { summary, breakdown, period } = earnings;

  // Prepare chart data
  const earningsBreakdownData = [
    {
      name: 'Transfer Fees',
      value: summary.transferFeeEarnings,
      color: theme.palette.warning.main,
    },
    {
      name: 'Electronic Levy',
      value: summary.electronicLevyEarnings,
      color: theme.palette.primary.main,
    },
    {
      name: 'Payscribe',
      value: summary.payscribeEarnings,
      color: theme.palette.info.main,
    },
  ];

  // Prepare trend data based on selected filter
  const prepareTrendData = () => {
    const allTransactions = [
      ...breakdown.transferFees.details.map(t => ({ ...t, type: 'Transfer Fee' })),
      ...breakdown.electronicLevy.details.map(t => ({ ...t, type: 'Electronic Levy' })),
      ...breakdown.payscribe.details.map(t => ({ ...t, type: 'Payscribe' })),
    ];

    // Group by date
    const groupedByDate = {};
    allTransactions.forEach(tx => {
      const txDate = new Date(tx.date);
      const dateKey = txDate.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = {
          date: dateKey,
          dateObj: txDate,
          'Transfer Fee': 0,
          'Electronic Levy': 0,
          'Payscribe': 0,
          total: 0,
        };
      }
      const profit = tx.profit || 0;
      groupedByDate[dateKey][tx.type] += profit;
      groupedByDate[dateKey].total += profit;
    });

    return Object.values(groupedByDate)
      .sort((a, b) => a.dateObj - b.dateObj)
      .map(item => {
        const { dateObj, ...rest } = item;
        return rest;
      });
  };

  const trendData = prepareTrendData();

  // Get chart title based on filter
  const getTrendChartTitle = () => {
    if (dataFilter === 'today') {
      return 'Earnings Trend (Today)';
    } else if (dataFilter === 'last_7_days') {
      return 'Earnings Trend (Last 7 Days)';
    } else if (dataFilter === 'last_30_days') {
      return 'Earnings Trend (Last 30 Days)';
    } else if (dataFilter === 'last_3_months') {
      return 'Earnings Trend (Last 3 Months)';
    } else if (dataFilter === 'last_6_months') {
      return 'Earnings Trend (Last 6 Months)';
    } else if (dataFilter === 'this_quarter') {
      return 'Earnings Trend (This Quarter)';
    } else if (dataFilter === 'last_quarter') {
      return 'Earnings Trend (Last Quarter)';
    } else if (dataFilter === 'this_year') {
      return 'Earnings Trend (This Year)';
    } else if (dataFilter === 'day' && filterDay && filterMonth && filterYear) {
      return `Earnings Trend (${new Date(filterYear, filterMonth - 1, filterDay).toLocaleDateString('en-NG', { month: 'long', day: 'numeric', year: 'numeric' })})`;
    } else if (dataFilter === 'month' && filterMonth && filterYear) {
      return `Earnings Trend (${new Date(filterYear, filterMonth - 1).toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })})`;
    } else if (dataFilter === 'year' && filterYear) {
      return `Earnings Trend (${filterYear})`;
    } else if (dataFilter === 'current_month') {
      return `Earnings Trend (${new Date().toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })})`;
    } else {
      return 'Earnings Trend (All Time)';
    }
  };

  // Category comparison data
  const categoryComparisonData = [
    {
      category: 'Transfer Fees',
      profit: summary.transferFeeEarnings,
      count: breakdown.transferFees.count,
      avgProfit: breakdown.transferFees.count > 0 ? summary.transferFeeEarnings / breakdown.transferFees.count : 0,
    },
    {
      category: 'Electronic Levy',
      profit: summary.electronicLevyEarnings,
      count: breakdown.electronicLevy.count,
      avgProfit: breakdown.electronicLevy.count > 0 ? summary.electronicLevyEarnings / breakdown.electronicLevy.count : 0,
    },
    {
      category: 'Payscribe',
      profit: summary.payscribeEarnings,
      count: breakdown.payscribe.count,
      avgProfit: breakdown.payscribe.count > 0 ? summary.payscribeEarnings / breakdown.payscribe.count : 0,
    },
  ];

  const COLORS = [
    theme.palette.warning.main,
    theme.palette.primary.main,
    theme.palette.info.main,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700} color={theme.palette.primary.main}>
          Earnings & Profit & Loss
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          alignItems: 'center', 
          flexWrap: 'wrap',
          bgcolor: theme.palette.background.paper,
          p: 1.5,
          borderRadius: 2,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={dataFilter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('all')}
              sx={{ minWidth: 90, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              All Time
            </Button>
            <Button
              variant={dataFilter === 'today' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('today')}
              sx={{ minWidth: 80, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Today
            </Button>
            <Button
              variant={dataFilter === 'last_7_days' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('last_7_days')}
              sx={{ minWidth: 100, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Last 7 Days
            </Button>
            <Button
              variant={dataFilter === 'last_30_days' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('last_30_days')}
              sx={{ minWidth: 110, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Last 30 Days
            </Button>
            <Button
              variant={dataFilter === 'current_month' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setDataFilter('current_month');
                const currentDate = new Date();
                setFilterMonth(currentDate.getMonth() + 1);
                setFilterYear(currentDate.getFullYear());
              }}
              sx={{ minWidth: 120, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              This Month
            </Button>
            <Button
              variant={dataFilter === 'last_3_months' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('last_3_months')}
              sx={{ minWidth: 120, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Last 3 Months
            </Button>
            <Button
              variant={dataFilter === 'last_6_months' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('last_6_months')}
              sx={{ minWidth: 120, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Last 6 Months
            </Button>
            <Button
              variant={dataFilter === 'this_quarter' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('this_quarter')}
              sx={{ minWidth: 110, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              This Quarter
            </Button>
            <Button
              variant={dataFilter === 'last_quarter' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('last_quarter')}
              sx={{ minWidth: 110, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Last Quarter
            </Button>
            <Button
              variant={dataFilter === 'this_year' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('this_year')}
              sx={{ minWidth: 100, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              This Year
            </Button>
            <Button
              variant={dataFilter === 'month' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('month')}
              sx={{ minWidth: 110, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Select Month
            </Button>
            <Button
              variant={dataFilter === 'year' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setDataFilter('year')}
              sx={{ minWidth: 100, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Select Year
            </Button>
            <Button
              variant={dataFilter === 'day' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setDataFilter('day');
                const currentDate = new Date();
                setFilterDay(currentDate.getDate());
                setFilterMonth(currentDate.getMonth() + 1);
                setFilterYear(currentDate.getFullYear());
              }}
              sx={{ minWidth: 100, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              Select Day
            </Button>
          </Box>
          
          {dataFilter === 'month' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
              <Select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                size="small"
                sx={{ minWidth: 140, bgcolor: theme.palette.background.default, borderRadius: 1.5 }}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                ))}
              </Select>
              <Select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                size="small"
                sx={{ minWidth: 100, bgcolor: theme.palette.background.default, borderRadius: 1.5 }}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </Box>
          )}
          
          {dataFilter === 'year' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
              <Select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                size="small"
                sx={{ minWidth: 100, bgcolor: theme.palette.background.default, borderRadius: 1.5 }}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {dataFilter === 'day' && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
              <TextField
                type="date"
                value={`${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(filterDay).padStart(2, '0')}`}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  setFilterYear(date.getFullYear());
                  setFilterMonth(date.getMonth() + 1);
                  setFilterDay(date.getDate());
                }}
                size="small"
                sx={{ 
                  minWidth: 150, 
                  bgcolor: theme.palette.background.default, 
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Earnings"
            value={formatCurrency(summary.totalEarnings)}
            icon={<TrendingUpIcon />}
            iconColor={theme.palette.success.main}
            subtitle="All profit sources combined"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="SafeHaven Earnings"
            value={formatCurrency(summary.safeHavenEarnings)}
            icon={<AccountBalanceIcon />}
            iconColor={theme.palette.primary.main}
            subtitle="Transfer Fees + Electronic Levy"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Transfer Fee Profit"
            value={formatCurrency(summary.transferFeeEarnings)}
            icon={<ReceiptIcon />}
            iconColor={theme.palette.warning.main}
            subtitle={`${breakdown.transferFees.count} transactions`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Payscribe Earnings"
            value={formatCurrency(summary.payscribeEarnings)}
            icon={<AttachMoneyIcon />}
            iconColor={theme.palette.info.main}
            subtitle={`${breakdown.payscribe.count} transactions`}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Earnings Breakdown Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
              Earnings Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={earningsBreakdownData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={800}
                  animationEasing="ease-out"
                >
                  {earningsBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 2,
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Earnings Trend Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
              {getTrendChartTitle()}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={trendData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorTransferFee" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.warning.main} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={theme.palette.warning.main} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorElectronicLevy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorPayscribe" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.info.main} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={theme.palette.info.main} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 2,
                  }}
                  labelStyle={{ color: theme.palette.text.primary }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="Transfer Fee" 
                  stackId="1" 
                  stroke={theme.palette.warning.main} 
                  fillOpacity={1}
                  fill="url(#colorTransferFee)"
                  animationDuration={1000}
                />
                <Area 
                  type="monotone" 
                  dataKey="Electronic Levy" 
                  stackId="1" 
                  stroke={theme.palette.primary.main} 
                  fillOpacity={1}
                  fill="url(#colorElectronicLevy)"
                  animationDuration={1000}
                />
                <Area 
                  type="monotone" 
                  dataKey="Payscribe" 
                  stackId="1" 
                  stroke={theme.palette.info.main} 
                  fillOpacity={1}
                  fill="url(#colorPayscribe)"
                  animationDuration={1000}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Category Comparison Bar Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}` }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color={theme.palette.text.primary}>
              Earnings by Category
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={categoryComparisonData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                <XAxis 
                  dataKey="category" 
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'profit') return [formatCurrency(value), 'Total Profit'];
                    if (name === 'count') return [value, 'Transactions'];
                    if (name === 'avgProfit') return [formatCurrency(value), 'Avg per Transaction'];
                    return [value, name];
                  }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 2,
                  }}
                  labelStyle={{ color: theme.palette.text.primary }}
                />
                <Legend />
                <Bar 
                  dataKey="profit" 
                  fill={theme.palette.success.main}
                  name="Total Profit"
                  radius={[8, 8, 0, 0]}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
                <Bar 
                  dataKey="avgProfit" 
                  fill={theme.palette.info.main}
                  name="Avg per Transaction"
                  radius={[8, 8, 0, 0]}
                  animationDuration={1200}
                  animationEasing="ease-out"
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Detailed Breakdown */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}` }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => {
            setSelectedTab(newValue);
            // Reset pagination when switching tabs
            setTransferFeePage(0);
            setElectronicLevyPage(0);
            setPayscribePage(0);
          }} 
          sx={{ mb: 3 }}
        >
          <Tab label={`Transfer Fees (${breakdown.transferFees.count})`} />
          <Tab label={`Electronic Levy (${breakdown.electronicLevy.count})`} />
          <Tab label={`Payscribe (${breakdown.payscribe.count})`} />
        </Tabs>

        {selectedTab === 0 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Username</strong></TableCell>
                    <TableCell align="right"><strong>Fee Charged</strong></TableCell>
                    <TableCell align="right"><strong>Deduction</strong></TableCell>
                    <TableCell align="right"><strong>Net Profit</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdown.transferFees.details.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No transfer fee transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    breakdown.transferFees.details
                      .slice(transferFeePage * transferFeeRowsPerPage, transferFeePage * transferFeeRowsPerPage + transferFeeRowsPerPage)
                      .map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{formatDate(item.date)}</TableCell>
                          <TableCell>{item.username || 'N/A'}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                          <TableCell align="right">
                            <Chip label={formatCurrency(item.deduction)} size="small" color="error" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {formatCurrency(item.profit)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell colSpan={4} align="right">
                      <Typography variant="subtitle1" fontWeight={700}>
                        Total Transfer Fee Profit:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        {formatCurrency(breakdown.transferFees.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={breakdown.transferFees.details.length}
              page={transferFeePage}
              onPageChange={(e, newPage) => setTransferFeePage(newPage)}
              rowsPerPage={transferFeeRowsPerPage}
              onRowsPerPageChange={(e) => {
                setTransferFeeRowsPerPage(parseInt(e.target.value, 10));
                setTransferFeePage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
            />
          </>
        )}

        {selectedTab === 1 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Username</strong></TableCell>
                    <TableCell align="right"><strong>Electronic Levy</strong></TableCell>
                    <TableCell align="right"><strong>Profit</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdown.electronicLevy.details.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No electronic levy transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    breakdown.electronicLevy.details
                      .slice(electronicLevyPage * electronicLevyRowsPerPage, electronicLevyPage * electronicLevyRowsPerPage + electronicLevyRowsPerPage)
                      .map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{formatDate(item.date)}</TableCell>
                          <TableCell>{item.username || 'N/A'}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {formatCurrency(item.profit)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell colSpan={3} align="right">
                      <Typography variant="subtitle1" fontWeight={700}>
                        Total Electronic Levy Profit:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        {formatCurrency(breakdown.electronicLevy.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={breakdown.electronicLevy.details.length}
              page={electronicLevyPage}
              onPageChange={(e, newPage) => setElectronicLevyPage(newPage)}
              rowsPerPage={electronicLevyRowsPerPage}
              onRowsPerPageChange={(e) => {
                setElectronicLevyRowsPerPage(parseInt(e.target.value, 10));
                setElectronicLevyPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
            />
          </>
        )}

        {selectedTab === 2 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Username</strong></TableCell>
                    <TableCell><strong>Purpose</strong></TableCell>
                    <TableCell><strong>Network/Provider</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell align="right"><strong>Commission Rate</strong></TableCell>
                    <TableCell align="right"><strong>Profit</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdown.payscribe.details.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No Payscribe transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    breakdown.payscribe.details
                      .slice(payscribePage * payscribeRowsPerPage, payscribePage * payscribeRowsPerPage + payscribeRowsPerPage)
                      .map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{formatDate(item.date)}</TableCell>
                          <TableCell>{item.username || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip label={item.purpose} size="small" color="primary" />
                          </TableCell>
                          <TableCell>{item.network || 'N/A'}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                          <TableCell align="right">{item.commissionRate}%</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {formatCurrency(item.profit)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell colSpan={6} align="right">
                      <Typography variant="subtitle1" fontWeight={700}>
                        Total Payscribe Profit:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        {formatCurrency(breakdown.payscribe.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={breakdown.payscribe.details.length}
              page={payscribePage}
              onPageChange={(e, newPage) => setPayscribePage(newPage)}
              rowsPerPage={payscribeRowsPerPage}
              onRowsPerPageChange={(e) => {
                setPayscribeRowsPerPage(parseInt(e.target.value, 10));
                setPayscribePage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Earnings;

